import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { 
  CCard, CCardHeader, CCardBody, CBadge, CButton, CTable, CTableHead, 
  CTableRow, CTableHeaderCell, CTableBody, CTableDataCell, CButtonGroup,
  CFormSelect, CSpinner 
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilWarning, cilCheckCircle, cilMediaPlay, cilHistory, cilFilter, cilActionUndo } from '@coreui/icons';
import dayjs from 'dayjs';

const StoreAlertsWidget = ({ storeCameras, onOpenAudioModal }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('unread'); // 'unread' или 'read'
  const [filterCategory, setFilterCategory] = useState('ALL'); // Фильтр по названию правила

  const fetchAlerts = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/audio-alerts/', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const storeAlerts = (res.data.results || []).filter(a => storeCameras.includes(a.camera));
      setAlerts(storeAlerts);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (storeCameras && storeCameras.length > 0) {
      fetchAlerts();
      const interval = setInterval(fetchAlerts, 30000);
      return () => clearInterval(interval);
    }
  }, [storeCameras]);

  const handleMarkReviewed = async (id, e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/audio-alerts/${id}/mark-reviewed/`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(prev => prev.map(a => a.id === id ? { ...a, is_reviewed: true } : a));
    } catch (e) {}
  };

  const handleMarkUnreviewed = async (id, e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`/api/audio-alerts/${id}/`, { is_reviewed: false }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(prev => prev.map(a => a.id === id ? { ...a, is_reviewed: false } : a));
    } catch (e) {}
  };

  const handleListenClick = (alert) => {
    const dummySegment = {
      id: alert.id,
      start_time: alert.timestamp,
      start_sec: alert.start_sec,
      audio_url: alert.audio_url,
      context_type: alert.segment_data?.context_type || 'Неизвестно',
      dialog_markers: alert.segment_data?.dialog_markers || {},
      topics: alert.segment_data?.topics || {},
      raw_lines: alert.raw_lines || [] 
    };
    onOpenAudioModal(dummySegment);
  };

  // --- ИНДИКАТОР ЗАГРУЗКИ ---
  if (loading) {
    return (
      <CCard className="mb-4 border-top-3 border-top-info shadow-sm">
        <CCardBody className="text-center py-4">
          <CSpinner color="primary" size="sm" className="me-2" />
          <span className="text-muted fw-bold">Загрузка уведомлений и нарушений...</span>
        </CCardBody>
      </CCard>
    );
  }

  // Если загрузка закончилась и алармов нет вообще
  if (alerts.length === 0) return null;

  const uniqueCategories = Array.from(new Set(alerts.map(a => a.title))).sort();
  const unreadAlerts = alerts.filter(a => !a.is_reviewed);
  const readAlerts = alerts.filter(a => a.is_reviewed);
  
  let displayedAlerts = filterStatus === 'unread' ? unreadAlerts : readAlerts;

  if (filterCategory !== 'ALL') {
    displayedAlerts = displayedAlerts.filter(a => a.title === filterCategory);
  }

  const redAlertsCount = unreadAlerts.filter(a => a.severity === 'red_alert').length;

  return (
    <CCard className={`mb-4 border-top-3 shadow-sm ${redAlertsCount > 0 ? 'border-top-danger' : 'border-top-warning'}`}>
      <CCardHeader className="bg-white d-flex justify-content-between align-items-center py-3 flex-wrap gap-3">
        <strong className="fs-5 d-flex align-items-center">
          <CIcon icon={cilWarning} size="xl" className={`me-2 ${redAlertsCount > 0 ? 'text-danger' : 'text-warning'}`} />
          Уведомления и Алармы ({unreadAlerts.length} новых)
        </strong>
        
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <div className="d-flex align-items-center gap-2">
            <CIcon icon={cilFilter} className="text-muted" />
            <CFormSelect 
              size="sm" 
              value={filterCategory} 
              onChange={(e) => setFilterCategory(e.target.value)}
              style={{ minWidth: '220px', cursor: 'pointer' }}
            >
              <option value="ALL">Все типы нарушений</option>
              {uniqueCategories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </CFormSelect>
          </div>

          <CButtonGroup>
            <CButton 
              color={filterStatus === 'unread' ? 'primary' : 'secondary'} 
              variant={filterStatus === 'unread' ? '' : 'outline'} 
              onClick={() => setFilterStatus('unread')}
              size="sm"
              className="fw-bold"
            >
              Новые ({unreadAlerts.length})
            </CButton>
            <CButton 
              color={filterStatus === 'read' ? 'primary' : 'secondary'} 
              variant={filterStatus === 'read' ? '' : 'outline'} 
              onClick={() => setFilterStatus('read')}
              size="sm"
              className="fw-bold"
            >
              <CIcon icon={cilHistory} className="me-1" />
              Прочитанные ({readAlerts.length})
            </CButton>
          </CButtonGroup>
        </div>
      </CCardHeader>

      <CCardBody className="p-0">
        <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
          {displayedAlerts.length === 0 ? (
            <div className="p-4 text-center text-muted">
              {filterStatus === 'unread' ? 'В этой категории нет новых уведомлений.' : 'В этой категории история пуста.'}
            </div>
          ) : (
            <CTable hover responsive align="middle" className="mb-0">
              <CTableHead color="light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <CTableRow>
                  <CTableHeaderCell>Время</CTableHeaderCell>
                  <CTableHeaderCell>Камера</CTableHeaderCell>
                  <CTableHeaderCell>Нарушение</CTableHeaderCell>
                  <CTableHeaderCell>Подробности</CTableHeaderCell>
                  <CTableHeaderCell className="text-center">Действия</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {displayedAlerts.map(alt => (
                  <CTableRow 
                    key={alt.id} 
                    className="cursor-pointer" 
                    onClick={() => handleListenClick(alt)}
                    style={{ opacity: alt.is_reviewed ? 0.7 : 1 }}
                  >
                    <CTableDataCell className="fw-bold text-nowrap">
                      {dayjs(alt.timestamp).format('DD.MM.YYYY HH:mm:ss')}
                    </CTableDataCell>
                    <CTableDataCell>{alt.camera_name}</CTableDataCell>
                    <CTableDataCell>
                      <CBadge color={alt.severity === 'red_alert' ? 'danger' : 'warning'}>
                        {alt.severity === 'red_alert' ? '🚨 КРИТИЧНО' : '⚠️ СКРИПТ'}
                      </CBadge>
                      <div className="mt-1 fw-bold">{alt.title}</div>
                    </CTableDataCell>
                    <CTableDataCell className="text-start" style={{ maxWidth: '400px' }}>
                      <div className="text-dark fst-italic">«{alt.quote}»</div>
                      <small className="text-muted">{alt.explanation}</small>
                    </CTableDataCell>
                    <CTableDataCell className="text-center text-nowrap">
                      <div className="d-flex flex-column gap-2 align-items-center">
                        <CButton size="sm" color="primary" variant="outline" className="w-100" onClick={(e) => { e.stopPropagation(); handleListenClick(alt); }}>
                          <CIcon icon={cilMediaPlay} className="me-1" /> Слушать
                        </CButton>
                        
                        {!alt.is_reviewed ? (
                          <CButton size="sm" color="success" className="text-white w-100" onClick={(e) => handleMarkReviewed(alt.id, e)}>
                            <CIcon icon={cilCheckCircle} className="me-1" /> Принято
                          </CButton>
                        ) : (
                          <CButton size="sm" color="secondary" variant="ghost" className="w-100" onClick={(e) => handleMarkUnreviewed(alt.id, e)}>
                            <CIcon icon={cilActionUndo} className="me-1" /> Вернуть
                          </CButton>
                        )}
                      </div>
                    </CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>
          )}
        </div>
      </CCardBody>
    </CCard>
  );
};

export default StoreAlertsWidget;